/**
 * Outil « Circuit RC » (Boîte à outils, physique-chimie Tle).
 *
 * Explorer : le schéma (générateur E, interrupteur à deux positions, R, C), les charges qui
 * s'accumulent sur les armatures et le courant qui faiblit ; la courbe u_C(t) ou i(t) qui se
 * trace pendant le film, avec τ lu à 63 % (charge) ou 37 % (décharge) et la tangente à l'origine.
 * Défi : six questions. Logique : `rc.ts`.
 */

import { useState, type SVGProps } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, ParamSlider, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { McqChallenge } from '../../ui/mcq-challenge';
import { PlayBar, usePlayer } from '../../ui/player';
import { RichText } from '../../ui/rich-text';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  C_VALUES,
  R_VALUES,
  ampText,
  coulombText,
  fr,
  makeQuiz,
  microF,
  ohm,
  quizKinds,
  rcAt,
  rcLines,
  span,
  tauOf,
  timeText,
  type Phase,
} from './rc';

const W = 340;
const INK = 'var(--color-ink-700)';
const MUTED = 'var(--color-ink-500)';
const ACCENT = 'var(--color-primary-500)';
const HOT = 'var(--color-error-500)';

function Lines({ lines }: { lines: string[] }) {
  return (
    <Note>
      {lines.map((l) => (
        <span key={l} className="mb-1 block last:mb-0" data-testid="rc-law">
          <RichText>{l}</RichText>
        </span>
      ))}
    </Note>
  );
}

function Readout({ rows }: { rows: Array<[string, string]> }) {
  return (
    <table className="w-full text-sm" data-testid="rc-readings">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-ink-100 border-b last:border-0">
            <th scope="row" className="text-ink-600 py-1.5 pr-2 text-left font-normal">
              <RichText>{k}</RichText>
            </th>
            <td className="text-ink-900 py-1.5 text-right font-semibold tabular-nums">
              {frenchSpaces(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** « u_C » en SVG */
function Sub({ base, sub, ...p }: { base: string; sub: string } & SVGProps<SVGTextElement>) {
  return (
    <HaloText {...p}>
      <tspan>{base}</tspan>
      <tspan baselineShift="sub" fontSize="0.75em">
        {sub}
      </tspan>
    </HaloText>
  );
}

function Circuit({
  phase,
  E,
  R,
  C,
  t,
}: {
  phase: Phase;
  E: number;
  R: number;
  C: number;
  t: number;
}) {
  const s = rcAt(phase, E, R, C, t);
  const i0 = E / R;
  const top = 34;
  const bottom = 118;
  const gx = 30;
  const c1 = { x: 96, y: top };
  const c2 = { x: 96, y: 66 };
  const pivot = { x: 128, y: top };
  const cx = 236;
  const plateY = (top + bottom) / 2;
  const nPlus = Math.round((s.q / (C * 1e-6 * E)) * 6);
  const level = Math.abs(s.i) / i0;
  return (
    <g data-testid="rc-circuit">
      {/* générateur */}
      <line x1={gx} y1={top} x2={gx} y2={plateY - 8} stroke={INK} strokeWidth={1.6} />
      <line
        x1={gx - 12}
        y1={plateY - 8}
        x2={gx + 12}
        y2={plateY - 8}
        stroke={INK}
        strokeWidth={2}
      />
      <line x1={gx - 6} y1={plateY + 2} x2={gx + 6} y2={plateY + 2} stroke={INK} strokeWidth={4} />
      <line x1={gx} y1={plateY + 2} x2={gx} y2={bottom} stroke={INK} strokeWidth={1.6} />
      <text x={gx + 16} y={plateY + 2} fontSize={11} fill={INK} fontWeight={700}>
        E
      </text>
      <text x={gx - 16} y={plateY - 10} fontSize={10} fill={MUTED}>
        +
      </text>
      {/* fils */}
      <line x1={gx} y1={top} x2={c1.x} y2={top} stroke={INK} strokeWidth={1.6} />
      <line x1={c2.x} y1={c2.y} x2={c2.x} y2={bottom} stroke={INK} strokeWidth={1.6} />
      <line x1={gx} y1={bottom} x2={cx} y2={bottom} stroke={INK} strokeWidth={1.6} />
      <circle cx={c1.x} cy={c1.y} r={3} fill={INK} />
      <circle cx={c2.x} cy={c2.y} r={3} fill={INK} />
      <text x={c1.x - 4} y={c1.y - 7} fontSize={10} fill={MUTED}>
        1
      </text>
      <text x={c2.x - 12} y={c2.y + 4} fontSize={10} fill={MUTED}>
        2
      </text>
      {/* interrupteur */}
      <circle cx={pivot.x} cy={pivot.y} r={3} fill={INK} />
      <line
        x1={pivot.x}
        y1={pivot.y}
        x2={phase === 'charge' ? c1.x + 2 : c2.x + 2}
        y2={phase === 'charge' ? c1.y : c2.y - 2}
        stroke={INK}
        strokeWidth={2.4}
        strokeLinecap="round"
        data-testid="rc-switch"
      />
      <text x={pivot.x - 4} y={top - 10} fontSize={10} fill={MUTED}>
        K
      </text>
      {/* résistance */}
      <line x1={pivot.x} y1={top} x2={146} y2={top} stroke={INK} strokeWidth={1.6} />
      <rect x={146} y={top - 8} width={46} height={16} fill="none" stroke={INK} strokeWidth={1.6} />
      <text x={169} y={top - 13} textAnchor="middle" fontSize={10} fill={INK} fontWeight={700}>
        {`R = ${ohm(R)}`}
      </text>
      <line x1={192} y1={top} x2={cx} y2={top} stroke={INK} strokeWidth={1.6} />
      {/* condensateur */}
      <line x1={cx} y1={top} x2={cx} y2={plateY - 5} stroke={INK} strokeWidth={1.6} />
      <line
        x1={cx - 18}
        y1={plateY - 5}
        x2={cx + 18}
        y2={plateY - 5}
        stroke={INK}
        strokeWidth={2.6}
      />
      <line
        x1={cx - 18}
        y1={plateY + 5}
        x2={cx + 18}
        y2={plateY + 5}
        stroke={INK}
        strokeWidth={2.6}
      />
      <line x1={cx} y1={plateY + 5} x2={cx} y2={bottom} stroke={INK} strokeWidth={1.6} />
      {Array.from({ length: nPlus }, (_, k) => (
        <g key={k}>
          <text x={cx - 15 + k * 6} y={plateY - 9} fontSize={9} fill={HOT} fontWeight={700}>
            +
          </text>
          <text x={cx - 15 + k * 6} y={plateY + 15} fontSize={9} fill={ACCENT} fontWeight={700}>
            −
          </text>
        </g>
      ))}
      <text x={cx + 22} y={plateY - 8} fontSize={10} fill={INK} fontWeight={700}>
        {`C = ${microF(C)}`}
      </text>
      <Sub
        base="u"
        sub="C"
        x={cx + 22}
        y={plateY + 12}
        fontSize={11}
        fill={ACCENT}
        fontWeight={700}
      />
      <text x={cx + 38} y={plateY + 12} fontSize={11} fill={ACCENT} fontWeight={700}>
        {`= ${fr(s.uC, 2)} V`}
      </text>
      {/* courant */}
      {level > 0.01 && (
        <g data-testid="rc-current" opacity={0.25 + 0.75 * level}>
          <path
            d={
              phase === 'charge'
                ? `M 204 ${top - 6} l 14 6 l -14 6 Z`
                : `M 218 ${top - 6} l -14 6 l 14 6 Z`
            }
            fill={HOT}
          />
          <text x={229} y={top + 20} textAnchor="end" fontSize={10} fill={HOT} fontWeight={700}>
            {`i = ${ampText(Math.abs(s.i))}`}
          </text>
        </g>
      )}
    </g>
  );
}

function Graph({
  phase,
  E,
  R,
  C,
  t,
  show,
}: {
  phase: Phase;
  E: number;
  R: number;
  C: number;
  t: number;
  show: 'u' | 'i';
}) {
  const tau = tauOf(R, C);
  const T = span(R, C);
  const L = 44;
  const Rr = W - 14;
  const top = 160;
  const B = 300;
  const i0 = E / R;
  const isU = show === 'u';
  const [lo, hi] = isU ? [0, E] : phase === 'charge' ? [0, i0] : [-i0, 0];
  const X = (time: number) => L + (time / T) * (Rr - L);
  const Y = (v: number) => B - ((v - lo) / (hi - lo)) * (B - top);
  const val = (time: number) => {
    const s = rcAt(phase, E, R, C, time);
    return isU ? s.uC : s.i;
  };
  const pts = (to: number) =>
    Array.from({ length: 121 }, (_, k) => (k / 120) * to)
      .map((time) => `${X(time)},${Y(val(time))}`)
      .join(' ');
  const v0 = val(0);
  const vInf = isU ? (phase === 'charge' ? E : 0) : 0;
  const mark = isU ? (phase === 'charge' ? 0.632 * E : 0.368 * E) : 0.368 * v0;
  return (
    <g data-testid="rc-graph">
      <line x1={L} y1={top} x2={L} y2={B} stroke="var(--color-ink-300)" />
      <line x1={L} y1={Y(0)} x2={Rr} y2={Y(0)} stroke="var(--color-ink-300)" />
      <Sub
        base={isU ? 'u' : 'i'}
        sub={isU ? 'C' : ''}
        x={L + 4}
        y={top - 6}
        fontSize={11}
        fill={INK}
        fontWeight={700}
      />
      <text x={L + 22} y={top - 6} fontSize={10} fill={MUTED}>
        {isU ? '(V)' : '(mA)'}
      </text>
      {[lo, hi]
        .filter((v) => v !== 0)
        .map((v) => (
          <text key={v} x={L - 4} y={Y(v) + 3.5} textAnchor="end" fontSize={9.5} fill={MUTED}>
            {isU ? fr(v, 1) : fr(v * 1000, 2)}
          </text>
        ))}
      <text x={L - 4} y={Y(0) + 3.5} textAnchor="end" fontSize={9.5} fill={MUTED}>
        0
      </text>
      {[1, 2, 3, 4, 5].map((k) => (
        <text key={k} x={X(k * tau)} y={B + 13} textAnchor="middle" fontSize={9.5} fill={MUTED}>
          {k === 1 ? 'τ' : `${k}τ`}
        </text>
      ))}
      <text x={Rr} y={B + 26} textAnchor="end" fontSize={10} fill={MUTED}>
        {`t (τ = ${timeText(tau)})`}
      </text>
      {/* asymptote, 63 % / 37 %, tangente à l'origine */}
      <line x1={L} y1={Y(vInf)} x2={Rr} y2={Y(vInf)} stroke={MUTED} strokeDasharray="5 4" />
      <line x1={L} y1={Y(mark)} x2={X(tau)} y2={Y(mark)} stroke={HOT} strokeDasharray="3 3" />
      <line x1={X(tau)} y1={Y(mark)} x2={X(tau)} y2={Y(0)} stroke={HOT} strokeDasharray="3 3" />
      <line
        x1={X(0)}
        y1={Y(v0)}
        x2={X(tau)}
        y2={Y(vInf)}
        stroke={ACCENT}
        strokeDasharray="4 3"
        opacity={0.8}
      />
      <HaloText
        x={X(tau) + 4}
        y={Y(mark) + (mark > (v0 + vInf) / 2 ? 14 : -5)}
        fontSize={10}
        fill={HOT}
        fontWeight={600}
      >
        {isU ? (phase === 'charge' ? '63 % de E' : '37 % de E') : '37 %'}
      </HaloText>
      <polyline points={pts(T)} fill="none" stroke={ACCENT} strokeWidth={1.5} opacity={0.25} />
      <polyline points={pts(t)} fill="none" stroke={ACCENT} strokeWidth={2.6} />
      <circle cx={X(t)} cy={Y(val(t))} r={5} fill={HOT} stroke="white" strokeWidth={1.5} />
    </g>
  );
}

export function RcTool({
  level = null,
  seed,
}: {
  /** niveau (Tle) */
  level?: string | null;
  /** graine du défi (tests) */
  seed?: number;
}) {
  const rank = schoolLevelRank(level);
  const [mode, setMode] = useState<'explore' | 'quiz'>('explore');
  const [phase, setPhase] = useState<Phase>('charge');
  const [ri, setRi] = useState(3);
  const [ci, setCi] = useState(6);
  const [E, setE] = useState(6);
  const [show, setShow] = useState<'u' | 'i'>('u');
  const R = R_VALUES[ri]!;
  const C = C_VALUES[ci]!;
  const T = span(R, C);
  const player = usePlayer(T, 8, `${phase}-${R}-${C}-${E}`);
  const s = rcAt(phase, E, R, C, player.t);
  const tau = tauOf(R, C);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Partie de l’outil"
        value={mode}
        options={[
          { value: 'explore', label: 'Explorer' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <div hidden={mode !== 'explore'} className="space-y-4">
        <Segmented
          ariaLabel="Position de l’interrupteur"
          value={phase}
          options={[
            { value: 'charge', label: 'Charge (K en 1)' },
            { value: 'discharge', label: 'Décharge (K en 2)' },
          ]}
          onChange={setPhase}
        />
        <svg
          viewBox={`0 0 ${W} 330`}
          className={`${FIGURE_BG_CLASS} w-full rounded-2xl`}
          role="img"
          aria-label={`${phase === 'charge' ? 'Charge' : 'Décharge'} : à t = ${timeText(player.t)}, u_C = ${fr(s.uC, 2)} V, i = ${ampText(s.i)}`}
          data-testid="rc-figure"
        >
          <Circuit phase={phase} E={E} R={R} C={C} t={player.t} />
          <Graph phase={phase} E={E} R={R} C={C} t={player.t} show={show} />
        </svg>
        <PlayBar player={player} T={T} format={timeText} />
        <Segmented
          ariaLabel="Courbe"
          value={show}
          options={[
            { value: 'u', label: 'Tension du condensateur' },
            { value: 'i', label: 'Intensité' },
          ]}
          onChange={setShow}
        />
        <Stepper
          label="Résistance R"
          value={ri}
          min={0}
          max={R_VALUES.length - 1}
          onChange={setRi}
          format={(k) => frenchSpaces(ohm(R_VALUES[k]!))}
        />
        <Stepper
          label="Capacité C"
          value={ci}
          min={0}
          max={C_VALUES.length - 1}
          onChange={setCi}
          format={(k) => frenchSpaces(microF(C_VALUES[k]!))}
        />
        <ParamSlider
          name="Tension du générateur E"
          ariaLabel="Tension E"
          value={E}
          min={1}
          max={12}
          step={0.5}
          onChange={setE}
          format={(v) => frenchSpaces(`${fr(v, 1)} V`)}
        />
        <Readout
          rows={[
            ['τ = R × C', timeText(tau)],
            ['Instant t', `${timeText(player.t)} = ${fr(player.t / tau, 2)} τ`],
            ['Tension u_C', `${fr(s.uC, 2)} V`],
            ['Tension u_R = R × i', `${fr(s.uR, 2)} V`],
            ['Intensité i', ampText(s.i)],
            ['Charge q = C × u_C', coulombText(s.q)],
          ]}
        />
        <Lines lines={rcLines(phase, E, R, C, player.t)} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <McqChallenge kinds={quizKinds(rank)} make={makeQuiz} seed={seed} testId="rc" rich />
      </div>
    </div>
  );
}
